import React, { useState, useEffect, useContext } from 'react';
import config from '../config';
import { Container, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Select, MenuItem, InputLabel, FormControl, Button } from '@mui/material';
import { AppContext } from '../App';
import { FilePenLine, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import ProductPlansManager from './ProductPlansManager';
import { trackEvent } from '../analytics';

const ProductPlanManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false); // Delayed loading indicator
  const [error, setError] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductDescription, setNewProductDescription] = useState('');
  const [newProductDemoUrl, setNewProductDemoUrl] = useState('');
  const [newProductApiUrl, setNewProductApiUrl] = useState('');
  const [newProductBlurb, setNewProductBlurb] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');

  // State for Edit Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // Store the whole product object
  const [editProductName, setEditProductName] = useState('');
  const [editProductDescription, setEditProductDescription] = useState('');
  const [editProductDemoUrl, setEditProductDemoUrl] = useState('');
  const [editProductApiUrl, setEditProductApiUrl] = useState('');
  const [editProductBlurb, setEditProductBlurb] = useState('');

  // State for managing plans
  const [selectedProductForPlans, setSelectedProductForPlans] = useState(null);
  const { selectedTeam } = useContext(AppContext); // If not already in your code

  const { userTeams } = useContext(AppContext);
  console.log('ProductPlanManagement - userTeams from context:', userTeams);

  // Only show loading indicator after 150ms delay to prevent flash
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowLoading(true), 150);
    } else {
      setShowLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/products/GetOwnedProducts?include_inactive=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData?.error || errorMsg;
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      if (data && data.success) {
        setProducts(data.products);
      } else {
        throw new Error(data?.error || 'Failed to fetch products');
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err.message || 'An error occurred while fetching products.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setNewProductName('');
    setNewProductDescription('');
    setNewProductDemoUrl('');
    setNewProductApiUrl('');
    setNewProductBlurb('');
    setSelectedTeamId(userTeams.length > 0 ? userTeams[0].teamId : '');
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleCreateProduct = async () => {
    setError(null); // Clear previous errors

    // Frontend validation
    if (!newProductName || newProductName.trim() === '') {
      setError('Product name cannot be empty.');
      return; // Prevent API call
    }
    if (!selectedTeamId) {
      setError('A team must be selected.'); // Should ideally not happen if UI is correct
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newProductName,
          description: newProductDescription,
          demo_url: newProductDemoUrl,
          api_url: newProductApiUrl,
          blurb: newProductBlurb,
          team_id: selectedTeamId
        })
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData?.error || errorMsg;
        } catch (e) { /* Ignore */ }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      if (data.success) {
        trackEvent('create_product');
        handleCloseCreateModal();
        fetchProducts(); // Refresh the product list
        // Optional: Add a success notification (e.g., Snackbar)
      } else {
        throw new Error(data?.error || 'Failed to create product');
      }

    } catch (err) {
      console.error("Error creating product:", err);
      setError(err.message || 'An error occurred while creating the product.');
      // Keep the modal open on error so the user can see the message or retry
    }
  };

  const handleOpenEditModal = (product) => {
    setEditingProduct(product);
    setEditProductName(product.name);
    setEditProductDescription(product.description || '');
    setEditProductDemoUrl(product.demo_url || '');
    setEditProductApiUrl(product.api_url || '');
    setEditProductBlurb(product.blurb || '');
    setError(''); // Clear errors
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingProduct(null);
    setError('');
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    if (!editProductName.trim()) {
      setError('Product name cannot be empty.');
      return;
    }
    setError('');
    setLoading(true); // Optional: indicate loading

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const payload = {
        name: editProductName,
        description: editProductDescription,
        demo_url: editProductDemoUrl,
        api_url: editProductApiUrl,
        blurb: editProductBlurb,
      };

      const response = await fetch(`${config.apiBaseUrl}/api/products/${editingProduct.id}`, {
        method: 'PATCH', // Use PATCH for partial updates
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData?.error || errorMsg;
        } catch (e) { /* Ignore */ }
        throw new Error(`Failed to update product: ${errorMsg}`);
      }

      const data = await response.json();
      if (data.success) {
        handleCloseEditModal();
        fetchProducts(); // Refresh list
      } else {
        throw new Error(data?.error || 'Failed to update product');
      }

    } catch (err) {
      console.error("Error updating product:", err);
      setError(err.message || 'An error occurred while updating the product.');
      // Keep modal open on error
    } finally {
      setLoading(false); // Optional: stop loading indicator
    }
  };

  const handleEditProduct = (productId, productName) => {
    console.log(`TODO: Edit product ${productName} (${productId})`);
    // Logic to open an edit modal will go here
  };

  const handleToggleProductStatus = async (productId, productName, currentStatus) => {
    const action = currentStatus ? 'Deactivate' : 'Activate';
    // Correct endpoint based on backend: POST with /deactivate or /reactivate suffix
    const endpoint = currentStatus 
      ? `${config.apiBaseUrl}/api/products/${productId}/deactivate` 
      : `${config.apiBaseUrl}/api/products/${productId}/reactivate`;
    // Correct method based on backend: POST for both actions
    const method = 'POST'; 

    // Optional: Add a confirmation dialog here if desired
    // if (!window.confirm(`Are you sure you want to ${action.toLowerCase()} the product "${productName}"?`)) {
    //   return;
    // }
    
    // Clear previous errors and set loading state if you have one
    setError(''); 
    // setLoading(true); // Assuming you have a setLoading state

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' // Required even for DELETE/PUT with no body sometimes
        },
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData?.error || errorMsg;
        } catch (e) { /* Ignore if response has no body or is not JSON */ }
        throw new Error(`Failed to ${action.toLowerCase()} product: ${errorMsg}`);
      }

      // Assuming success if response is OK (e.g., 200 or 204)
      console.log(`Product "${productName}" ${action.toLowerCase()}d successfully.`);
      fetchProducts(); // Refresh the product list

    } catch (err) {
      console.error(`Error ${action.toLowerCase()}ing product:`, err);
      setError(err.message || `An error occurred while ${action.toLowerCase()}ing the product.`);
    } finally {
      // setLoading(false); // Turn off loading state
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const copyToClipboard = async (value) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-slate-100">
          Product & Plan Management
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Define products and pricing plans for your API keys.{' '}
          <a href="/docs/cloud-integration.html" className="text-indigo-400 hover:text-indigo-300">
            Learn more →
          </a>
        </p>
      </div>

      <button 
        onClick={handleOpenCreateModal} 
        className={`bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded mb-4 ${userTeams.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={userTeams.length === 0}
      >
        Create New Product
      </button>
      {userTeams.length === 0 && !loading && (
        <p className="text-sm text-slate-400 mb-4">
          You must belong to a team to create products.
        </p>
      )}

      {loading && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12">
          {showLoading ? (
            <div className="flex justify-center items-center">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-slate-400">Loading products...</span>
            </div>
          ) : (
            <div className="h-6" />
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-3 rounded-lg mb-4" role="alert">
          <strong className="font-medium">Error:</strong>
          <span className="ml-2">{error}</span>
        </div>
      )}
      
      {!loading && !error && (
        <div className="bg-slate-800 shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider w-2/5">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Team
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {products.length > 0 ? (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-700/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      <div>{product.name}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                        <span className="font-mono break-all">Product ID: {product.id}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(product.id)}
                          className="p-1 rounded hover:bg-white/10 text-slate-300 hover:text-white transition"
                          title="Copy product ID"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 max-w-md break-words space-y-2">
                      {product.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                      {product.team_name} 
                    </td>
                    <td>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.is_active ? 'bg-green-900 text-green-100' : 'bg-red-900 text-red-100'}`}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                      <button 
                        onClick={() => handleOpenEditModal(product)} // Pass the whole product object
                        className="text-blue-400 hover:text-blue-300 focus:outline-none"
                        title="Edit Product"
                      >
                        <FilePenLine size={18} />
                      </button>
                      
                      {product.is_active ? (
                        <button 
                          onClick={() => handleToggleProductStatus(product.id, product.name, product.is_active)}
                          className="text-yellow-400 hover:text-yellow-300 focus:outline-none"
                          title="Deactivate Product"
                        >
                          <ToggleLeft size={18} />
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleToggleProductStatus(product.id, product.name, product.is_active)}
                          className="text-green-400 hover:text-green-300 focus:outline-none"
                          title="Activate Product"
                        >
                          <ToggleRight size={18} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => setSelectedProductForPlans(product)}
                        className="text-indigo-400 hover:text-indigo-300 focus:outline-none"
                        title="Manage Plans"
                      >
                        Manage Plans
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-slate-500">
                    No products found for your teams.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Plan Management UI - Moved outside the table */}
          {selectedProductForPlans && (
            <div className="mt-8 bg-slate-800 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-white">
                  Manage Plans for: {selectedProductForPlans.name}
                </h3>
                <div className="text-xs text-slate-400 mr-auto pl-1">
                  <span className="font-mono">Product ID: {selectedProductForPlans.id}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedProductForPlans.id)}
                    className="ml-2 p-1 rounded hover:bg-white/10 text-slate-300 hover:text-white transition"
                    title="Copy product ID"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <button
                  onClick={() => setSelectedProductForPlans(null)}
                  className="px-3 py-1 bg-slate-700 text-slate-200 rounded hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
              <ProductPlansManager
                selectedProduct={selectedProductForPlans}
                teamId={selectedTeam?.teamId}
              />
            </div>
          )}
        </div>
      )}

      <Dialog open={createModalOpen} onClose={handleCloseCreateModal}>
        <DialogTitle>Create New Product</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the details for the new product.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Product Name"
            type="text"
            fullWidth
            variant="standard"
            value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="description"
            label="Description (Optional, internal)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="standard"
            value={newProductDescription}
            onChange={(e) => setNewProductDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            id="blurb"
            label="Customer blurb (shown to prospects in demo bundles)"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="standard"
            value={newProductBlurb}
            onChange={(e) => setNewProductBlurb(e.target.value)}
          />
          <TextField
            margin="dense"
            id="demo_url"
            label="Demo URL (human UI, usually hosts API docs)"
            type="url"
            fullWidth
            variant="standard"
            placeholder="https://demo.yourproduct.example"
            value={newProductDemoUrl}
            onChange={(e) => setNewProductDemoUrl(e.target.value)}
          />
          <TextField
            margin="dense"
            id="api_url"
            label="API URL (machine endpoint)"
            type="url"
            fullWidth
            variant="standard"
            placeholder="https://api.yourproduct.example"
            value={newProductApiUrl}
            onChange={(e) => setNewProductApiUrl(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="team-select-label">Team</InputLabel>
            <Select
              labelId="team-select-label"
              id="team-select"
              value={selectedTeamId}
              label="Team"
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              {userTeams.map((team) => (
                <MenuItem key={team.teamId} value={team.teamId}>
                  {team.teamName} 
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal}>Cancel</Button>
          <Button 
            onClick={handleCreateProduct} 
            disabled={!newProductName || !selectedTeamId}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editModalOpen} onClose={handleCloseEditModal}>
        <DialogTitle>Edit Product: {editingProduct?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the updated details for the product.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="edit-name"
            label="Product Name"
            type="text"
            fullWidth
            variant="standard"
            value={editProductName}
            onChange={(e) => setEditProductName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-description"
            label="Description (Optional, internal)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="standard"
            value={editProductDescription}
            onChange={(e) => setEditProductDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-blurb"
            label="Customer blurb (shown to prospects in demo bundles)"
            type="text"
            fullWidth
            multiline
            rows={2}
            variant="standard"
            value={editProductBlurb}
            onChange={(e) => setEditProductBlurb(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-demo-url"
            label="Demo URL (human UI, usually hosts API docs)"
            type="url"
            fullWidth
            variant="standard"
            placeholder="https://demo.yourproduct.example"
            value={editProductDemoUrl}
            onChange={(e) => setEditProductDemoUrl(e.target.value)}
          />
          <TextField
            margin="dense"
            id="edit-api-url"
            label="API URL (machine endpoint)"
            type="url"
            fullWidth
            variant="standard"
            placeholder="https://api.yourproduct.example"
            value={editProductApiUrl}
            onChange={(e) => setEditProductApiUrl(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="team-select-label">Team</InputLabel>
            <Select
              labelId="team-select-label"
              id="team-select"
              value={editingProduct?.team_id}
              label="Team"
              disabled
            >
              <MenuItem value={editingProduct?.team_id}>
                {editingProduct?.team_name} 
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Cancel</Button>
          <Button onClick={handleUpdateProduct}>Save Changes</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default ProductPlanManagement;
